import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../base/Button';

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showExpertModal, setShowExpertModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setScrolled(scrollPosition > 50);

      const sections = [
        'home',
        'services',
        'about',
        'products',
        'government-orders',
        'subsidies',
        'testimonials',
        'contact',
      ];
      const headerHeight = 80;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (!section) continue;

        const sectionTop = section.offsetTop - headerHeight;
        const sectionBottom = sectionTop + section.offsetHeight;

        if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
          if (activeSection !== sections[i]) setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          const headerHeight = 80;
          window.scrollTo({
            top: section.offsetTop - headerHeight,
            behavior: 'smooth',
          });
          setActiveSection(sectionId);
        }
      }, 300);
      setIsMenuOpen(false);
      return;
    }

    const section = document.getElementById(sectionId);
    if (!section) return;

    const headerHeight = 80;
    const elementPosition = section.offsetTop - headerHeight;

    window.scrollTo({
      top: elementPosition,
      behavior: 'smooth',
    });

    setActiveSection(sectionId);
    setIsMenuOpen(false);
  };

  const navigateToContact = () => {
    navigate('/contact');
    setIsMenuOpen(false);
  };

  const handleEmailClick = () => {
    window.location.href =
      'mailto:info@djtevpowertech.com?subject=Inquiry%20about%20EV%20Charging%20Solutions&body=Hello%20DJT%20HAIKA%20team,%0D%0A%0D%0AI%20am%20interested%20in%20learning%20more%20about%20your%20EV%20charging%20solutions.%0D%0A%0D%0APlease%20contact%20me%20with%20more%20information.%0D%0A%0D%0AThank%20you!';
  };

  const handleGetQuote = () => {
    window.location.href = 'tel:18005700229';
  };

  const handlePhoneClick = () => {
    window.location.href = 'tel:18005700229';
  };

  const handleContactExpert = () => {
    setShowExpertModal(true);
  };

  const handlePhoneCall = () => {
    window.location.href = 'tel:18005700229';
    setShowExpertModal(false);
  };

  const handleEmailExpert = () => {
    window.location.href =
      'mailto:info@djtevpowertech.com?subject=Expert%20Consultation%20Request&body=Hello%20DJT%20HAIKA%20team,%0D%0A%0D%0AI%20need%20expert%20consultation%20for%20EV%20charging%20solutions.%0D%0A%0D%0APlease%20contact%20me%20at%20your%20earliest%20convenience.%0D%0A%0D%0AThank%20you!';
    setShowExpertModal(false);
  };

  const handleScheduleCall = () => {
    navigate('/contact');
    setShowExpertModal(false);
  };

  const handleWhatsAppChat = () => {
    window.open(
      'https://wa.me/919154673422?text=Hi,%20I%20need%20expert%20consultation%20for%20EV%20charging%20solutions.%20Please%20help%20me%20choose%20the%20right%20charging%20infrastructure%20for%20my%20needs.',
      '_blank'
    );
    setShowExpertModal(false);
  };

  const handleLiveChat = () => {
    const chatBot =
      document.querySelector('[class*="chat"]') ||
      document.querySelector('button[class*="chat"]');
    if (chatBot) (chatBot as HTMLElement).click();
    setShowExpertModal(false);
  };

  // Desktop nav items config
  const desktopNavItems = [
    { id: 'home', label: 'Welcome', icon: 'ri-home-4-line', isPage: false },
    { id: 'services', label: 'Services', icon: 'ri-flashlight-line', isPage: false },
    { id: 'about', label: 'About', icon: 'ri-information-line', isPage: false },
    { id: 'products', label: 'Products', icon: 'ri-car-line', isPage: false },
    { id: 'government-orders', label: 'Gov Orders', icon: 'ri-government-line', isPage: false },
    { id: 'subsidies', label: 'Subsidies', icon: 'ri-money-rupee-circle-line', isPage: false },
    { id: 'contact', label: 'Contact', icon: 'ri-customer-service-line', isPage: true },
  ];

  // Tablet nav items config
  const tabletNavItems = [
    { id: 'home', icon: 'ri-home-4-line', label: 'Welcome', isPage: false },
    { id: 'services', icon: 'ri-flashlight-line', label: 'Services', isPage: false },
    { id: 'products', icon: 'ri-car-line', label: 'Products', isPage: false },
    { id: 'government-orders', icon: 'ri-government-line', label: 'Orders', isPage: false },
    { id: 'subsidies', icon: 'ri-money-rupee-circle-line', label: 'Subsidies', isPage: false },
    { id: 'contact', icon: 'ri-customer-service-line', label: 'Contact', isPage: true },
  ];

  // Mobile nav items config
  const mobileNavItems = [
    { id: 'home', label: 'Welcome', icon: 'ri-home-4-line', isPage: false },
    { id: 'services', label: 'Services', icon: 'ri-flashlight-line', isPage: false },
    { id: 'about', label: 'About', icon: 'ri-information-line', isPage: false },
    { id: 'products', label: 'Products', icon: 'ri-car-line', isPage: false },
    { id: 'government-orders', label: 'Government Orders', icon: 'ri-government-line', isPage: false },
    { id: 'subsidies', label: 'EV Subsidies', icon: 'ri-money-rupee-circle-line', isPage: false },
    { id: 'contact', label: 'Contact', icon: 'ri-customer-service-line', isPage: true },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 w-full backdrop-blur-md shadow-lg border-b z-[9999] transition-all duration-300 relative overflow-hidden ${
          scrolled
            ? 'bg-slate-900/95 border-cyan-500/30'
            : 'bg-slate-900/85 border-cyan-500/20'
        }`}
        style={{ position: 'fixed' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Top contact bar */}
          <div
            className={`hidden md:flex items-center justify-between text-sm border-b py-2 transition-all duration-300 ${
              scrolled ? 'border-cyan-500/20' : 'border-cyan-500/15'
            }`}
          >
            <div className="flex items-center space-x-6 text-gray-300">
              <div
                className="flex items-center hover:text-cyan-400 transition-colors duration-300 cursor-pointer group"
                onClick={handleEmailClick}
              >
                <i className="ri-mail-line text-cyan-400 mr-2 text-sm"></i>
                <span className="text-xs">info@djtevpowertech.com</span>
              </div>

              <div
                className="flex items-center hover:text-green-400 transition-colors duration-300 cursor-pointer group"
                onClick={handlePhoneClick}
              >
                <i className="ri-phone-line text-green-400 mr-2 text-sm"></i>
                <span className="text-xs">1-800-570-0229</span>
              </div>

              <div className="flex items-center text-blue-400">
                <i className="ri-map-pin-line mr-2 text-sm"></i>
                <span className="text-xs">
                  India • Pune • Chennai • Delhi • Germany • Europe
                </span>
              </div>

              <div className="flex items-center text-blue-400">
                <i className="ri-customer-service-2-line mr-2 text-sm"></i>
                <span className="text-xs">24/7 Support</span>
              </div>
            </div>

            <div className="flex items-center space-x-1">
              {[
                {
                  icon: 'ri-facebook-fill',
                  url: 'https://facebook.com/djthaika',
                  color: 'text-blue-500 hover:text-blue-400',
                },
                {
                  icon: 'ri-twitter-fill',
                  url: 'https://twitter.com/djthaika',
                  color: 'text-sky-500 hover:text-sky-400',
                },
                {
                  icon: 'ri-linkedin-fill',
                  url: 'https://linkedin.com/company/djthaika',
                  color: 'text-blue-600 hover:text-blue-500',
                },
                {
                  icon: 'ri-instagram-fill',
                  url: 'https://instagram.com/djthaika',
                  color: 'text-pink-500 hover:text-pink-400',
                },
              ].map((item, idx) => (
                <a
                  key={idx}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${item.color} text-lg mx-1 transition-colors duration-300`}
                >
                  <i className={item.icon}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Main header */}
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3 hover:scale-105 transition-transform duration-300">
              <img
                src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/1465110942c76789698a862651f928ae.png"
                alt="DJT EV POWER TECH PVT LTD Logo"
                className="w-12 h-12 object-contain hover:brightness-110 transition-all duration-300 rounded-lg"
              />
              <div className="flex flex-col">
                <span
                  className="text-white font-medium text-sm leading-tight tracking-wide company-name-animated whitespace-nowrap"
                  style={{ fontFamily: 'Arial, sans-serif' }}
                >
                  DJT EV POWER TECH PVT LTD
                </span>
                <span className="text-cyan-400 text-xs font-medium whitespace-nowrap">
                  AI-Powered EV Solutions
                </span>
              </div>
            </div>

            {/* Desktop navigation - Redesigned for better spacing */}
            <nav className="hidden xl:flex items-center space-x-1">
              {desktopNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => item.isPage ? navigateToContact() : scrollToSection(item.id)}
                  className={`relative font-medium transition-all duration-300 rounded-lg px-2.5 py-1.5 text-xs whitespace-nowrap hover:scale-105 group overflow-hidden ${
                    activeSection === item.id
                      ? 'text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-md shadow-cyan-500/20 border border-cyan-400/30'
                      : 'text-gray-300 hover:text-white hover:bg-gradient-to-r hover:from-slate-700/60 hover:to-slate-600/60 border border-transparent hover:border-cyan-500/20'
                  }`}
                >
                  <span className="flex items-center relative z-10">
                    <i
                      className={`${item.icon} mr-1 text-xs transition-all duration-300 ${
                        activeSection === item.id ? 'text-cyan-200' : 'text-cyan-400 group-hover:text-cyan-300'
                      }`}
                    ></i>
                    <span className="font-semibold text-xs">{item.label}</span>
                  </span>
                  {activeSection === item.id && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4 h-0.5 bg-cyan-300 rounded-full animate-pulse"></div>
                  )}
                </button>
              ))}
            </nav>

            {/* Tablet navigation - Enhanced */}
            <nav className="hidden lg:flex xl:hidden items-center space-x-0.5">
              {tabletNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => item.isPage ? navigateToContact() : scrollToSection(item.id)}
                  className={`relative font-medium transition-all duration-300 rounded-lg px-2 py-1.5 whitespace-nowrap hover:scale-105 group ${
                    activeSection === item.id
                      ? 'text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-md border border-cyan-400/30'
                      : 'text-gray-300 hover:text-white hover:bg-slate-700/50 border border-transparent hover:border-cyan-500/20'
                  }`}
                >
                  <span className="flex flex-col items-center relative z-10">
                    <i
                      className={`${item.icon} text-xs mb-0.5 transition-all duration-300 ${
                        activeSection === item.id ? 'text-cyan-200' : 'text-cyan-400 group-hover:text-cyan-300'
                      }`}
                    ></i>
                    <span className="text-xs font-semibold">{item.label}</span>
                  </span>
                  {activeSection === item.id && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-3 h-0.5 bg-cyan-300 rounded-full"></div>
                  )}
                </button>
              ))}
            </nav>

            {/* CTA buttons */}
            <div className="hidden lg:flex items-center space-x-2">
              <div className="flex items-center space-x-2 px-2 py-1 bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30">
                <div className="w-4 h-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center relative">
                  <i className="ri-user-star-line text-white text-xs"></i>
                  <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                </div>
                <div className="text-left">
                  <p className="text-white text-xs font-medium">Expert Online</p>
                </div>
                <button
                  onClick={handleContactExpert}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium transition-all duration-300 whitespace-nowrap hover:scale-105 group text-xs px-2 py-1 rounded-md cursor-pointer"
                >
                  <i className="ri-customer-service-2-line mr-1"></i>
                  Talk
                </button>
              </div>

              <Button
                size="sm"
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-medium transition-all duration-300 whitespace-nowrap hover:scale-105 text-xs px-3 py-1.5"
                onClick={handleGetQuote}
              >
                <i className="ri-phone-line mr-1"></i>
                Quote
              </Button>
            </div>

            {/* Mobile menu toggle */}
            <button
              className="lg:hidden rounded-lg bg-slate-800/60 border border-cyan-500/30 transition-all duration-300 p-2 hover:scale-105 hover:bg-slate-700/80 group"
              onClick={toggleMenu}
            >
              <i
                className={`text-cyan-400 transition-all duration-300 text-lg ${
                  isMenuOpen ? 'ri-close-line' : 'ri-menu-4-line'
                }`}
              ></i>
            </button>
          </div>

          {/* Mobile navigation */}
          <div
            className={`lg:hidden transition-all duration-300 overflow-hidden ${
              isMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="py-4 space-y-2 border-t border-cyan-500/20 bg-slate-800/50 backdrop-blur-sm rounded-b-xl mx-2">
              {mobileNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => item.isPage ? navigateToContact() : scrollToSection(item.id)}
                  className={`flex items-center w-full text-left px-3 py-2.5 font-medium transition-all duration-300 rounded-lg mx-2 hover:scale-[1.02] group relative overflow-hidden ${
                    activeSection === item.id
                      ? 'text-white bg-gradient-to-r from-cyan-500/80 to-blue-600/80 shadow-lg border border-cyan-400/30'
                      : 'text-gray-300 hover:text-white hover:bg-gradient-to-r hover:from-slate-700/60 hover:to-slate-600/60 border border-transparent hover:border-cyan-500/20'
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full mr-3 transition-all duration-300 ${
                      activeSection === item.id ? 'bg-cyan-300 shadow-lg shadow-cyan-300/50 animate-pulse' : 'bg-gray-600 group-hover:bg-cyan-400'
                    }`}
                  ></div>
                  <div className="w-5 h-5 flex items-center justify-center mr-3 relative">
                    <i
                      className={`${item.icon} text-base transition-all duration-300 ${
                        activeSection === item.id ? 'text-cyan-200 drop-shadow-sm' : 'text-cyan-400 group-hover:text-cyan-300'
                      }`}
                    ></i>
                  </div>
                  <span className="font-semibold tracking-wide">{item.label}</span>
                  {activeSection === item.id && (
                    <div className="absolute right-3 w-2 h-2 bg-cyan-300 rounded-full animate-pulse"></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              ))}

              {/* Mobile actions */}
              <div className="px-3 py-3 space-y-2 border-t border-cyan-500/20 mt-2">
                <div
                  className="flex items-center text-sm text-gray-300 cursor-pointer hover:text-cyan-300 transition-colors duration-300 group py-2"
                  onClick={handleEmailClick}
                >
                  <div className="w-4 h-4 flex items-center justify-center mr-3 relative">
                    <i className="ri-mail-line text-cyan-400"></i>
                  </div>
                  <span>info@djtevpowertech.com</span>
                </div>

                <div
                  className="flex items-center text-sm text-gray-300 cursor-pointer hover:text-green-300 transition-colors duration-300 group py-2"
                  onClick={handlePhoneClick}
                >
                  <div className="w-4 h-4 flex items-center justify-center mr-3 relative">
                    <i className="ri-phone-line text-green-400"></i>
                  </div>
                  <span>1-800-570-0229</span>
                </div>

                <div className="flex items-center space-x-2 p-2 bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30 mt-3">
                  <div className="w-5 h-5 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center relative">
                    <i className="ri-user-star-line text-white text-xs"></i>
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse opacity-75"></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-xs font-semibold">
                      Expert Available - 24/7 Support
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold hover:scale-105 transition-all duration-300 text-xs"
                    onClick={handleContactExpert}
                  >
                    <i className="ri-customer-service-2-line mr-1"></i>
                    Talk to Expert
                  </Button>

                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-semibold hover:scale-105 transition-all duration-300 text-xs"
                    onClick={handleGetQuote}
                  >
                    <i className="ri-phone-line mr-1"></i>
                    Get Quote
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Expert contact modal */}
      {showExpertModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full mx-4 p-6 md:p-8 relative animate-scale-in shadow-2xl max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowExpertModal(false)}
              className="absolute top-4 right-4 w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl z-10"
            >
              <i className="ri-close-line text-lg"></i>
            </button>

            <div className="text-center">
              <div className="w-16 md:w-20 h-16 md:h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 relative">
                <i className="ri-user-star-line text-2xl md:text-3xl text-white"></i>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full animate-pulse">
                  <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-60"></div>
                </div>
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
                Connect with Our EV Expert
              </h3>

              <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed px-2">
                Get instant expert guidance for your EV charging needs. Our specialists are
                available 24/7 to help you make the right choice.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
                <button
                  className="flex items-center justify-center space-x-2 md:space-x-3 bg-green-600 hover:bg-green-700 py-3 md:py-4 px-4 md:px-6 rounded-xl text-white font-semibold transition-all duration-300 transform hover:scale-105 cursor-pointer shadow-lg hover:shadow-xl text-sm md:text-base"
                  onClick={handlePhoneCall}
                >
                  <i className="ri-phone-line text-lg md:text-xl"></i>
                  <div className="text-left">
                    <div className="text-sm font-medium">Call Now</div>
                    <div className="text-xs opacity-90">1-800-570-0229</div>
                  </div>
                </button>

                <button
                  className="flex items-center justify-center space-x-2 md:space-x-3 bg-purple-600 hover:bg-purple-700 py-3 md:py-4 px-4 md:px-6 rounded-xl text-white font-semibold transition-all duration-300 transform hover:scale-105 cursor-pointer shadow-lg hover:shadow-xl text-sm md:text-base"
                  onClick={handleEmailExpert}
                >
                  <i className="ri-mail-line text-lg md:text-xl"></i>
                  <div className="text-left">
                    <div className="text-sm font-medium">Email Expert</div>
                    <div className="text-xs opacity-90">Quick Response</div>
                  </div>
                </button>

                <button
                  className="flex items-center justify-center space-x-2 md:space-x-3 bg-cyan-600 hover:bg-cyan-700 py-3 md:py-4 px-4 md:px-6 rounded-xl text-white font-semibold transition-all duration-300 transform hover:scale-105 cursor-pointer shadow-lg hover:shadow-xl text-sm md:text-base"
                  onClick={handleLiveChat}
                >
                  <i className="ri-chat-3-line text-lg md:text-xl"></i>
                  <div className="text-left">
                    <div className="text-sm font-medium">Live Chat</div>
                    <div className="text-xs opacity-90">AI Assistant</div>
                  </div>
                </button>

                <button
                  className="flex items-center justify-center space-x-2 md:space-x-3 bg-blue-600 hover:bg-blue-700 py-3 md:py-4 px-4 md:px-6 rounded-xl text-white font-semibold transition-all duration-300 transform hover:scale-105 cursor-pointer shadow-lg hover:shadow-xl text-sm md:text-base"
                  onClick={handleWhatsAppChat}
                >
                  <i className="ri-whatsapp-line text-lg md:text-xl"></i>
                  <div className="text-left">
                    <div className="text-sm font-medium">WhatsApp</div>
                    <div className="text-xs opacity-90">Instant Chat</div>
                  </div>
                </button>
              </div>

              <button
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 py-3 md:py-4 px-4 md:px-6 rounded-xl text-white font-bold text-base md:text-lg transition-all duration-300 transform hover:scale-105 cursor-pointer shadow-lg hover:shadow-xl mb-4 md:mb-6"
                onClick={handleScheduleCall}
              >
                <i className="ri-calendar-line mr-2 md:mr-3 text-lg md:text-xl"></i>
                Schedule Site Visit &amp; Consultation
              </button>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 md:p-6 border border-green-200">
                <div className="flex items-center justify-center mb-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse mr-3"></div>
                  <p className="text-base md:text-lg text-green-800 font-bold">
                    Expert Available Now
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-xs md:text-sm text-green-700 mb-3">
                  <div className="text-center">
                    <div className="font-semibold">Response Time</div>
                    <div className="text-green-600">Under 2 minutes</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">Consultation</div>
                    <div className="text-green-600">100% Free</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 text-xs text-green-600">
                  <span className="flex items-center">
                    <i className="ri-check-line mr-1"></i>
                    5+ Years Experience
                  </span>
                  <span className="flex items-center">
                    <i className="ri-check-line mr-1"></i>
                    1500+ Projects
                  </span>
                  <span className="flex items-center">
                    <i className="ri-check-line mr-1"></i>
                    24/7 Support
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }

        /* Stylish Company Name Animation */
        @keyframes company-name-glow {
          0%, 100% {
            text-shadow: 0 0 5px rgba(255, 255, 255, 0.3), 0 0 10px rgba(34, 211, 238, 0.2), 0 0 15px rgba(34, 211, 238, 0.1);
            transform: scale(1);
          }
          50% {
            text-shadow: 0 0 8px rgba(255, 255, 255, 0.5), 0 0 15px rgba(34, 211, 238, 0.4), 0 0 20px rgba(34, 211, 238, 0.3);
            transform: scale(1.02);
          }
        }

        @keyframes company-name-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        @keyframes company-name-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }

        @keyframes company-name-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-1px); }
        }

        .company-name-animated {
          position: relative;
          background: linear-gradient(
            90deg,
            #ffffff 25%,
            #22d3ee 50%,
            #ffffff 75%
          );
          background-size: 200% 100%;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation:
            company-name-shimmer 3s ease-in-out infinite,
            company-name-glow 2s ease-in-out infinite,
            company-name-float 4s ease-in-out infinite;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .company-name-animated:hover {
          animation-duration: 1.5s, 1s, 2s;
          transform: scale(1.05) translateY(-2px);
          text-shadow:
            0 0 10px rgba(255, 255, 255, 0.6),
            0 0 20px rgba(34, 211, 238, 0.5),
            0 0 30px rgba(34, 211, 238, 0.4);
        }

        /* Additional glow effect on hover */
        .company-name-animated::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            90deg,
            transparent 30%,
            rgba(34, 211, 238, 0.1) 50%,
            transparent 70%
          );
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }

        .company-name-animated:hover::before {
          opacity: 1;
          animation: company-name-shimmer 1s ease-in-out infinite;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .company-name-animated {
            animation-duration: 4s, 3s, 5s;
          }
        }
      `}</style>
    </>
  );
}
